import React, { useState } from 'react';
import { 
  Box, 
  Chip,
  Typography,
  Button,
  Tooltip,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TankIcon from '@mui/icons-material/Water';
import TimelineIcon from '@mui/icons-material/Timeline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TuneIcon from '@mui/icons-material/Tune';

// 分割されたコンポーネント
import PriceSection from './trading/PriceSection';
import PredictionSection from './prediction/PredictionSection';
import FunctionControlDialog from './FunctionControlDialog';
import ProductEditDialog from './ProductEditDialog';
import UpdateRemainingDialog from './UpdateRemainingDialog';

/**
 * 🚀 修正版：製品の詳細情報を表示するメインコンポーネント
 * - ダイアログ完了後は単純にデータ更新のみ
 * - 余計なダイアログの再表示を防止
 */
const ProductDetails = ({ 
  selectedProduct, 
  onUpdateClick, 
  onEditClick,
  onShowMessage,
  onFunctionUpdate // 🆕 機能更新用コールバック
}) => {
  // 各種ダイアログの状態
  const [functionControlOpen, setFunctionControlOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  
  // 価格更新トリガー管理
  const [priceRefreshTrigger, setPriceRefreshTrigger] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!selectedProduct) return null;

  // 🚀 修正：確実なBoolean変換による機能フラグの取得
  const isTankFunctionEnabled = Boolean(
    selectedProduct.tankInstalled === true || 
    selectedProduct.tankInstalled === 1 || 
    selectedProduct.tankInstalled === "true"
  );
  const isPredictionFunctionEnabled = Boolean(
    selectedProduct.predictionEnabled === true || 
    selectedProduct.predictionEnabled === 1 || 
    selectedProduct.predictionEnabled === "true"
  );
  
  // 🚀 修正：確実なBoolean変換によるサービスモードの判定
  const getServiceMode = () => {
    if (isTankFunctionEnabled && isPredictionFunctionEnabled) {
      return 'PREDICTION_AUTO'; // フル機能
    } else if (isTankFunctionEnabled && !isPredictionFunctionEnabled) {
      return 'TANK_MANUAL'; // タンク+手動管理
    } else if (!isTankFunctionEnabled && isPredictionFunctionEnabled) {
      return 'PREDICTION_ONLY'; // 予測のみ
    } else {
      return 'PRICING_ONLY'; // 単価のみ
    }
  };

  const serviceMode = getServiceMode();

  // サービスモード別の設定
  const modeConfig = {
    'PREDICTION_AUTO': {
      icon: <AutoAwesomeIcon />,
      label: 'フル機能',
      color: 'success'
    },
    'TANK_MANUAL': {
      icon: <TankIcon />,
      label: 'タンク管理',
      color: 'primary'
    },
    'PREDICTION_ONLY': {
      icon: <TimelineIcon />,
      label: '予測機能',
      color: 'info'
    },
    'PRICING_ONLY': {
      icon: <MonetizationOnIcon />,
      label: '基本モード',
      color: 'warning'
    }
  };

  const config = modeConfig[serviceMode];

  // 🚀 編集ダイアログ制御 - サービスモードに応じて表示制御
  const handleEditClick = () => {
    const canEdit = serviceMode !== 'PRICING_ONLY';
    
    if (canEdit) {
      setEditDialogOpen(true);
    } else {
      // 基本モードでは編集ダイアログを開かない
      onShowMessage?.('機能設定からタンク管理または予測機能を有効にしてください', 'info');
    }
  };

  // 🚀 残量更新ダイアログ制御 - 予測機能有効時のみ
  const handleUpdateClick = () => {
    const canUpdate = serviceMode === 'PREDICTION_ONLY' || serviceMode === 'PREDICTION_AUTO';
    
    if (canUpdate) {
      setUpdateDialogOpen(true);
    } else {
      // 予測機能が無効な場合は更新ダイアログを開かない
      onShowMessage?.('残量更新には予測機能が必要です', 'info');
    }
  };

  // 🆕 価格更新成功時の処理
  const handlePriceUpdateSuccess = (message) => {
    setPriceRefreshTrigger(prev => prev + 1);
    // 価格更新時は成功メッセージを非表示
  };

  // 🆕 価格更新エラー時の処理
  const handlePriceUpdateError = (message) => {
    onShowMessage?.(message, 'error');
  };

  // 🚀 修正：データ更新処理 - 製品情報更新後にデータを再取得
  const handleDataUpdate = () => {
    // 製品情報更新時は機能更新用のコールバックを使用
    if (onFunctionUpdate) {
      onFunctionUpdate(); // ClientDetail.jsxのrefreshClientProductDataが呼ばれる
    } else if (onUpdateClick) {
      onUpdateClick(); // フォールバック
    }
  };

  // 🆕 機能制御ダイアログ更新後の処理
  const handleFunctionUpdate = () => {
    // 🚀 修正：親から渡されたコールバックを使用（window.location.reloadを回避）
    if (onFunctionUpdate) {
      onFunctionUpdate();
    } else {
      window.location.reload(); // フォールバック
    }
  };

  // 🚀 サービスモードステータス表示（簡潔版）
  const renderServiceModeStatus = () => {
    return (
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Chip
          icon={config.icon}
          label={config.label}
          color={config.color}
          variant="filled"
          sx={{ 
            fontWeight: 'bold',
            '& .MuiChip-icon': { fontSize: '18px' }
          }}
        />
        
        <Tooltip title="機能設定を変更">
          <Button
            variant="outlined"
            size="small"
            startIcon={<TuneIcon />}
            onClick={() => setFunctionControlOpen(true)}
            sx={{
              minWidth: 'auto',
              px: isMobile ? 1 : 2,
              fontSize: isMobile ? '0.75rem' : '0.875rem'
            }}
          >
            {isMobile ? '設定' : '機能設定'}
          </Button>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box>
      {/* サービスモードステータス表示 */}
      {renderServiceModeStatus()}

      {/* 🔹 取引情報セクション */}
      <PriceSection 
        selectedProduct={selectedProduct}
        priceRefreshTrigger={priceRefreshTrigger}
        onPriceUpdateSuccess={handlePriceUpdateSuccess}
        onPriceUpdateError={handlePriceUpdateError}
      />

      {/* 🔹 予測・タンク情報セクション */}
      <PredictionSection 
        selectedProduct={selectedProduct}
        serviceMode={serviceMode}
        isTankFunctionEnabled={isTankFunctionEnabled}
        isPredictionFunctionEnabled={isPredictionFunctionEnabled}
        onUpdateClick={handleUpdateClick} // 🚀 修正：制御されたハンドラーを使用
        onEditClick={handleEditClick}     // 🚀 修正：制御されたハンドラーを使用
        onFunctionControlClick={() => setFunctionControlOpen(true)}
      />

      {/* 🚀 機能制御ダイアログ */}
      <FunctionControlDialog
        open={functionControlOpen}
        onClose={() => setFunctionControlOpen(false)}
        selectedProduct={selectedProduct}
        onUpdate={handleFunctionUpdate}
        onShowMessage={onShowMessage}
      />

      {/* 🚀 サービスモード対応編集ダイアログ */}
      {editDialogOpen && (
        <ProductEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          clientProduct={selectedProduct}
          selectedClientProductId={selectedProduct?.id}
          onUpdate={handleDataUpdate} // 🚀 修正：シンプルなデータ更新のみ
          serviceMode={serviceMode}
        />
      )}

      {/* 🚀 サービスモード対応残量更新ダイアログ */}
      {updateDialogOpen && (
        <UpdateRemainingDialog
          open={updateDialogOpen}
          onClose={() => setUpdateDialogOpen(false)}
          clientProduct={selectedProduct}
          selectedClientProductId={selectedProduct?.id}
          onUpdate={handleDataUpdate} // 🚀 修正：シンプルなデータ更新のみ
          serviceMode={serviceMode}
        />
      )}
    </Box>
  );
};

export default ProductDetails;